import React from 'react';
import { useLang } from '../../i18n/LanguageContext';
import { waLink } from '../../data/contact';
import { inr, lumpValue, short, sipValue } from '../../utils/money';

const LIMITS = { sip: { min: 500, max: 100000, step: 500 }, lump: { min: 5000, max: 5000000, step: 5000 } };
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, Number.isFinite(v) ? v : lo));

function Field({ label, value, onChange, min, max, step, prefix, suffix }) {
  return (
    <div>
      <div className="flex justify-between items-center gap-3 mb-2">
        <label className="text-sm font-semibold">{label}</label>
        <div className="flex items-center bg-sky rounded-lg px-2 py-1 text-brand font-bold tabular-nums">
          {prefix}
          <input type="number" inputMode="numeric" min={min} max={max} step={step} value={value} aria-label={label}
            onChange={(e) => onChange(clamp(parseFloat(e.target.value), min, max))}
            className={`bg-transparent text-right outline-none ${prefix ? 'w-24' : 'w-12'}`} />
          {suffix && <span className="ml-1">{suffix}</span>}
        </div>
      </div>
      <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(+e.target.value)} className="w-full accent-brand" aria-label={label} />
    </div>
  );
}

// SIP / lump-sum calculator with a ring chart, a year-by-year table and a WhatsApp hand-off.
export default function SipLumpTool({ mode, onMode, amount, setAmount, rate, setRate, years, setYears }) {
  const { lang, t } = useLang();
  const lim = LIMITS[mode];
  const valAt = (y) => (mode === 'sip' ? sipValue(amount, rate, y) : lumpValue(amount, rate, y));
  const invAt = (y) => (mode === 'sip' ? amount * 12 * y : amount);
  const value = valAt(years);
  const invested = invAt(years);
  const C = 2 * Math.PI * 46;

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-3xl shadow-sm border border-slate-200 p-5 sm:p-7">
        <div className="inline-flex bg-slate-100 rounded-xl p-1 text-sm font-semibold mb-6" role="tablist">
          {['sip', 'lump'].map((m) => (
            <button key={m} type="button" role="tab" aria-selected={mode === m} onClick={() => onMode(m)}
              className={`px-4 py-2 rounded-lg transition ${mode === m ? 'bg-white text-brand shadow' : 'text-slate-500'}`}>{t(m === 'sip' ? 'tabSip' : 'tabLump')}</button>
          ))}
        </div>
        <div className="grid md:grid-cols-[1.2fr_1fr] gap-8 items-center">
          <div className="space-y-6">
            <Field label={t(mode === 'sip' ? 'monthly' : 'lumpAmt')} value={amount} onChange={setAmount} min={lim.min} max={lim.max} step={lim.step} prefix="₹" />
            <Field label={t('retLbl')} value={rate} onChange={setRate} min={1} max={20} step={0.5} suffix="%" />
            <Field label={t('period')} value={years} onChange={setYears} min={1} max={40} step={1} suffix={t('yrs')} />
          </div>
          <div className="flex flex-col items-center">
            <svg viewBox="0 0 120 120" className="w-44 h-44 sm:w-48 sm:h-48 -rotate-90" aria-hidden="true">
              <circle cx="60" cy="60" r="46" fill="none" stroke="#C7E9D8" strokeWidth="18" />
              <circle cx="60" cy="60" r="46" fill="none" stroke="#1D5FBF" strokeWidth="18" strokeDasharray={`${(C * invested) / value} ${C}`} style={{ transition: 'stroke-dasharray .5s ease' }} />
            </svg>
            <dl className="mt-4 w-full space-y-2 text-sm tabular-nums">
              <div className="flex justify-between"><dt className="flex items-center gap-2 text-slate-500"><i className="w-2.5 h-2.5 rounded-full bg-brand block" />{t('invested')}</dt><dd className="font-bold">{inr(invested)}</dd></div>
              <div className="flex justify-between"><dt className="flex items-center gap-2 text-slate-500"><i className="w-2.5 h-2.5 rounded-full bg-[#C7E9D8] block" />{t('gains')}</dt><dd className="font-bold text-emerald-600">{inr(value - invested)}</dd></div>
              <div className="flex justify-between pt-2 border-t border-slate-100 text-base"><dt className="font-semibold">{t('total')}</dt><dd className="font-extrabold text-lg">{inr(value)}</dd></div>
            </dl>
          </div>
        </div>
        <div className="mt-7 flex flex-col sm:flex-row gap-3 sm:items-center justify-between bg-emerald-50 border border-emerald-100 rounded-2xl p-4">
          <p className="text-xs text-slate-500 max-w-xl">{t('mfRisk')} {t('illusOnly')}</p>
          <a href={waLink(t('waCalc')(inr(amount), years, short(value, lang)))} target="_blank" rel="noopener noreferrer"
            className="shrink-0 text-center bg-emerald-500 hover:bg-emerald-600 text-white font-bold px-5 py-3 rounded-xl">{t('discuss')}</a>
        </div>
      </div>

      <div className="bg-white rounded-3xl shadow-sm border border-slate-200 p-5 sm:p-7">
        <h2 className="font-bold text-lg mb-4">{t('yearTbl')}</h2>
        <div className="max-h-80 overflow-auto">
          <table className="w-full text-sm tabular-nums">
            <thead className="text-slate-500 text-left sticky top-0 bg-white"><tr><th className="py-2 font-semibold">{t('year')}</th><th className="py-2 font-semibold">{t('invested')}</th><th className="py-2 font-semibold">{t('value')}</th><th className="py-2 w-1/3"><span className="sr-only">chart</span></th></tr></thead>
            <tbody>
              {Array.from({ length: years }, (_, k) => {
                const y = k + 1; const v = valAt(y); const iv = invAt(y);
                return (
                  <tr key={y} className="border-t border-slate-100">
                    <td className="py-2">{y}</td><td className="py-2">{short(iv, lang)}</td><td className="py-2 font-semibold">{short(v, lang)}</td>
                    <td className="py-2"><div className="h-2 rounded-full bg-slate-100 overflow-hidden flex"><i className="block h-full bg-brand" style={{ width: `${(iv / value) * 100}%` }} /><i className="block h-full bg-emerald-300" style={{ width: `${((v - iv) / value) * 100}%` }} /></div></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <article className="bg-white rounded-3xl shadow-sm border border-slate-200 p-5 sm:p-7">
        <h2 className="font-bold text-xl">{t('whatSip')}</h2>
        <p className="mt-3 text-slate-600 leading-relaxed">{t('whatSipP')}</p>
      </article>
    </div>
  );
}
